import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BackButton,
  SubmitButton,
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../../partials/Loader";

const EditPermissions = () => {
  const { id } = useParams();
  const [permissionDetails, setPermissionDetails] = useState({
    permission_name: "",
    group: "",
    status: 1,
  });
  const [loader, setLoader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    permission_name: Yup.string()
      .required("Permission name is required")
      .min(3, "Minimum 3 characters")
      .max(50, "Max 50 characters")
      .matches(/^[^\d]/, "Cannot start with a number")
      .matches(/^[a-zA-Z0-9-_]+$/, "Only letters, numbers, - and _ allowed"),
    group: Yup.string()
      .required("Group is required")
      .min(2, "Minimum 2 characters"),
    status: Yup.number().required("Status is required"),
  });

  const fetchPermissionDetails = async () => {
    try {
      const response = await fetchData(
        "permission/" + id,
        "GET",
        null,
        {},
        {},
        "/permission",
        dispatch
      );

      const data = response?.data;
      if (data) {
        setPermissionDetails({
          permission_name: data.permission_name ?? "",
          group: data.group ?? "",
          status: data.status ?? 0,
        });
      }
    } catch (error) {
      console.error("Error fetching permission details:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchPermissionDetails();
  }, [id]);

  const formik = useFormik({
    initialValues: permissionDetails,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      setIsSubmitting(true);
      try {
        const payload = {
          id: id,
          permission_name: values.permission_name,
          group: values.group,
          status: values.status,
        };

        const response = await fetchData("permission", "PUT", payload);

        if (response.success) {
          toast.success("Permission updated successfully.");
          resetForm();
          navigate("/manage-permissions");
        } else {
          //duplication error
          if (
            response?.message &&
            response.message.toLowerCase().includes("already exists")
          ) {
            setFieldError("permission_name", response.message);
          } else {
            toast.error(response.message || "Failed to update permission.");
          }
        }
      } catch (error) {
        const apiMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error updating permission.";

        //Inline duplication error
        if (apiMessage.toLowerCase().includes("already exists")) {
          formik.setFieldError("permission_name", apiMessage);
        } else {
          toast.error(apiMessage);
        }

        console.error("Update error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (loader) return <Loader />;

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Permission
        </h1>
        <BackButton to="/manage-permissions" text="Back" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Permission
          </h1>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="flex lg:flex-nowrap flex-wrap gap-5">
            <ThinTextField
              id="permission_name"
              label="Permission Name"
              placeholder="e.g., deh-create"
              type="text"
              value={formik.values.permission_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.permission_name &&
                Boolean(formik.errors.permission_name)
              }
              helperText={
                formik.touched.permission_name && formik.errors.permission_name
              }
            />

            <ThinTextField
              id="group"
              label="Group"
              placeholder="e.g., deh"
              type="text"
              value={formik.values.group}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.group && Boolean(formik.errors.group)}
              helperText={formik.touched.group && formik.errors.group}
            />

            <ThinDropdownWithSearch
              label="Status"
              id="status-dropdown"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(value) =>
                formik.setFieldValue("status", Number(value))
              }
              onBlur={formik.handleBlur}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
            />
          </div>

          <SubmitButton
            type="submit"
            text={isSubmitting ? "Submitting..." : "Update"}
            disabled={isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};

export default EditPermissions;
